import React, { useEffect, useMemo, useState } from 'react';
import { BookOpenText, CheckCheck, Filter, Newspaper, RefreshCcw } from 'lucide-react';

import useToast from '../hooks/useToast';
import { API_URL } from '../config';
import NewsDetailModal from '../components/NewsDetailModal';
import { getNewsList, markAllNewsRead, markNewsRead, markNewsViewed } from '../services/newsApi';
import { formatTimezoneDate, TIMEZONE_CHANGED_EVENT } from '../utils/timezone';
import { haptic } from '../utils/vibration';

const formatDate = (value) => {
  return formatTimezoneDate(value, false);
};

const CATEGORY_META = {
  news: {
    label: 'Новость',
    icon: Newspaper,
    badgeClassName: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:ring-blue-900/50'
  },
  instruction: {
    label: 'Инструкция',
    icon: BookOpenText,
    badgeClassName: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200 dark:ring-emerald-900/50'
  },
  update: {
    label: 'Обновление',
    icon: RefreshCcw,
    badgeClassName: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:ring-amber-900/50'
  }
};

const resolveNewsImageUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:') || !raw.startsWith('/')) {
    return raw;
  }
  return `${API_URL}${raw}`;
};

const FILTER_OPTIONS = [
  { id: 'all', label: 'Все' },
  { id: 'instruction', label: 'Инструкции' },
  { id: 'update', label: 'Обновления' },
  { id: 'news', label: 'Новости' }
];

const News = () => {
  const { toast, showToast, hideToast } = useToast(3200);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [, setTimezoneVersion] = useState(0);
  const unreadCount = useMemo(() => items.filter((item) => !item.is_read).length, [items]);
  const filteredItems = useMemo(
    () => items.filter((item) => activeFilter === 'all' || (item.category || 'news') === activeFilter),
    [activeFilter, items]
  );

  const fetchNews = async () => {
    setLoading(true);
    try {
      const data = await getNewsList();
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error('Ошибка загрузки новостей:', err);
      showToast(err.message || 'Не удалось загрузить новости', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  useEffect(() => {
    const handleTimezoneChanged = () => {
      setTimezoneVersion((prev) => prev + 1);
    };

    window.addEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChanged);
    return () => window.removeEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChanged);
  }, []);

  const handleOpen = (item) => {
    haptic('selection');
    setSelectedNews(item);
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      markNewsViewed(item.id).catch((error) => {
        console.error('Ошибка учёта просмотра новости:', error);
      });
    }
  };

  const handleRead = async () => {
    if (!selectedNews || selectedNews.is_read) {
      setSelectedNews(null);
      return;
    }

    try {
      await markNewsRead(selectedNews.id);
      setItems((prev) => prev.map((item) => (
        item.id === selectedNews.id ? { ...item, is_read: true } : item
      )));
      haptic('success');
    } catch (err) {
      console.error('Ошибка отметки новости:', err);
      showToast(err.message || 'Не удалось отметить новость', 'error');
      return;
    }

    setSelectedNews(null);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNewsRead();
      setItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
      haptic('success');
    } catch (err) {
      console.error('Ошибка отметки новостей:', err);
      showToast(err.message || 'Не удалось отметить новости', 'error');
    }
  };

  return (
    <div className="space-y-4 desktop-content-container">
      <div className="rounded-3xl bg-white p-4 shadow-sm dark:bg-gray-900">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                setShowFilters((prev) => !prev);
              }}
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border transition-colors ${
                showFilters || activeFilter !== 'all'
                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
              aria-label="Фильтры новостей"
              title="Фильтры"
            >
              <Filter className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={!unreadCount}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              <CheckCheck className="h-4 w-4" />
              Прочитать всё
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  haptic('selection');
                  setActiveFilter(option.id);
                  setShowFilters(false);
                }}
                className={`inline-flex items-center rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                  activeFilter === option.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="h-28 animate-pulse rounded-3xl bg-white shadow-sm dark:bg-gray-900" />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-3xl bg-white p-10 text-center shadow-sm dark:bg-gray-900">
          <Newspaper className="mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-700" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Для выбранного фильтра пока ничего нет.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => {
            const categoryMeta = CATEGORY_META[item.category || 'news'] || CATEGORY_META.news;
            const CategoryIcon = categoryMeta.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleOpen(item)}
                className={`w-full overflow-hidden rounded-3xl border bg-white text-left shadow-sm transition hover:shadow-md dark:bg-gray-900 ${
                  item.is_read
                    ? 'border-gray-200 dark:border-gray-800'
                    : 'border-blue-200 ring-1 ring-blue-100 dark:border-blue-900/50 dark:ring-blue-900/30'
                }`}
              >
                <div className="flex gap-3 p-3.5">
                  {item.title_image_url ? (
                    <img src={resolveNewsImageUrl(item.title_image_url)} alt={item.title} className="h-20 w-24 shrink-0 rounded-2xl object-cover" />
                  ) : (
                    <div className="flex h-20 w-24 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
                      <CategoryIcon className="h-5 w-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${categoryMeta.badgeClassName}`}>
                          <CategoryIcon className="h-3.5 w-3.5" />
                          {categoryMeta.label}
                        </div>
                        <h3 className="mt-2 line-clamp-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{item.title}</h3>
                      </div>
                      {!item.is_read ? <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" /> : null}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">{formatDate(item.created_at)}</div>
                  </div>
                </div>
              </button>
          )})}
        </div>
      )}

      <NewsDetailModal
        news={selectedNews}
        isOpen={!!selectedNews}
        onClose={() => setSelectedNews(null)}
        onConfirm={handleRead}
        confirmLabel={selectedNews?.is_read ? 'Закрыть' : 'Понятно'}
      />

          </div>
  );
};

export default News;
